import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DestinationAPI } from '../../../../../edge/destinations/api/DestinationAPI';
import { ExistingEdgeDestinationTilesFactory } from '../../../../../edge/destinations/create-destination/factories/existingEdgeDestinationTilesFactory';
import { DestinationListFactory } from '../../../../../edge/destinations/factories/destinationFactory';
import { UpgradePipelineBanner } from '../../../../../edge/pipelines/UpgradePipeline/UpgradePipelineBanner';
import { DESTINATION_TYPES } from '../../../../destination/constants/destinationTypes';
import { DestinationMode } from '../../../../../core/interfaces/destination-mode';
import { HevoEntity } from '../../../../../core/interfaces/hevo-entity';
import { matchPattern } from '../../../../../core/utils/string';
import { getDestinationModeKeywords } from '../../../../../connector/connector-form/utils/utils';
import {
  TRACKER_ADD_NEW_DESTINATION_CLICK,
  TRACKER_CLICK_DISABLED_DESTINATION, TRACKER_COPY_STANDARD_DESTINATION_CLICK,
  TRACKER_DESTINATION_LIST_EMPTY_RESULT,
  TRACKER_DESTINATION_PERMISSION_VALIDATION_RETRY_CLICK,
  TRACKER_SELECT_EXISTING_DEST_TYPE
} from '../../../../../connector/connector-form/tracking/connectorFormTracking';
import { DestinationVersions } from '../../../../destination/interfaces/destination';
import { TRACKER_SELECT_EXISTING_DRAFT_DESTINATION_ITEM } from '../../../tracking/pipelineTracking';
import { NoEntityTypeAlert } from '../../../../../fallback-screens/components/no-entity-type-alert/NoEntityTypeAlert';
import RetryApiAlert from '../../../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import SearchArea from '../../../../../list/components/search-area/SearchArea';
import StickySearchWrapper from '../../../../../list/components/sticky-search-wrapper/StickySearchWrapper';
import { WizardStepBaseProps } from '../../../../../wizard/interfaces/wizardStep';
import { useWizardStepData } from '../../../../../wizard/hooks/useWizardStepData';
import { getDataIdGenerator } from '../../../../../core/utils/generateDataId';
import { DestinationTile } from '../select-destination-tile/SelectDestinationTile';
import { TileGrid } from '@hevoio/hevo-ui-toolkit';
import { DEFAULT_TILE_HEIGHT } from '../../../../../layout/constants/tileGrid';
import { TileSkeletonGrid } from '@hevoio/hevo-ui-toolkit';
import { HdButton, HdDocLink, HdPane, HdRbacButton } from '../../../../../components';
import useAbortController from '../../../../../core/hooks/useAbortController';
import useAnalyticsTracker from '../../../../../analytics/hooks/useAnalyticsTracker';
import useTeamSettingsService from '../../../../team/hooks/useTeamSettingsService';
import StandardDestinationsAPI from '../../../../destination/api/DestinationsAPI';
import styles from './SelectDestination.module.scss';
import { sortDestinationsBasedOnError } from './utils';
import DestinationTilesFactory from '../../factories/selectDestinationTilesFactory';
import { RbacPermissions } from '../../../../../core/interfaces/user';
import { useConfirm } from '@hevoio/hevo-ui-toolkit';
import { IconDatabasePlus } from '@tabler/icons-react';

export const DEBOUNCE_INTERVAL = 500;
const INITIAL_PERMISSIONS = {
  validatingPermissionForDestId: null,
  validatingPermission: false,
  permissionsError: false
};

export const dataIdGenerator = getDataIdGenerator('destinationList');

export function SelectDestination({
  initialData,
  controller,
  completeStep,
  onDataChange,
  skip,
  markDirty
}: WizardStepBaseProps) {
  const [data, setData] = useWizardStepData(initialData, onDataChange);

  const destinationMode = controller.data.destinationMode || DestinationMode.DESTINATION;
  const destinationKeywords = getDestinationModeKeywords(destinationMode);

  const onCreateNewClick = () => {
    if (data.selectedDestination) {
      markDirty();
    }

    setData(s => ({
      ...s,
      selectedDestination: undefined
    }));

    completeStep();
  };

  const onDestinationSelect = dest => {
    if (!data.selectedDestination || data.selectedDestination.id !== dest.id) {
      markDirty();
    }

    setData(s => ({
      ...s,
      selectedDestination: dest
    }));

    completeStep();
  };

  const saveSearch = useCallback(term => {
    setData(prev => ({
      ...prev,
      _search: term
    }));
  }, []);

  return (
    <SelectDestinationBase
      versions={[DestinationVersions.STANDARD]}
      copyModeDestinations={[]}
      isUpgradingPipeline={false}
      standardPipelineInfo={undefined}
      destinationKeywords={destinationKeywords}
      destinationMode={destinationMode}
      hevoEntityType={controller.params.hevoEntityType || HevoEntity.DESTINATION}
      defaultSearch={data._search}
      filterMethod={controller.data?.filterMethod}
      onDestinationSelect={onDestinationSelect}
      createNewClick={onCreateNewClick}
      onDestinationCopy={undefined}
      saveSearch={saveSearch}
      skipStep={skip}
    />
  );
}

export function SelectDestinationBase({
  versions,
  isUpgradingPipeline,
  copyModeDestinations,
  standardPipelineInfo,
  destinationKeywords,
  destinationMode,
  onDestinationSelect,
  onDestinationCopy,
  hevoEntityType,
  createNewClick,
  defaultSearch,
  filterMethod,
  saveSearch,
  skipStep
}) {
  const [allDestinations, setAllDestinations] = useState([]);
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [searchTerm, setSearchTerm] = useState(defaultSearch);
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissionsObject, setPermissionsObject] = useState(INITIAL_PERMISSIONS);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const analyticsTracker = useAnalyticsTracker();
  const teamSettingsService = useTeamSettingsService();
  const { abortController, abort } = useAbortController();
  const { confirm } = useConfirm();
  const draftPipelineSettingsEnabled =
    !!teamSettingsService.getTeamSettings()?.draft_pipeline_enabled || false;

  const showPageError = !!apiError;
  const showNoMatchAlert = filteredDestinations.length === 0 && !loading && !apiError;
  const suggestionContext = {
    name: destinationKeywords.singular,
    value: destinationMode
  };

  const getDestinations = async () => {
    if (apiError) {
      setApiError(null);
    }

    setLoading(true);

    try {
      const shouldIncludeDraftStandardDestinations = versions.includes(DestinationVersions.STANDARD)
        && hevoEntityType.toString() === HevoEntity.PIPELINE.toString()
        && !copyModeDestinations.length
        && draftPipelineSettingsEnabled;

      let [standardDestinations, edgeDestinations, draftDestinations] = await Promise.all([
        versions.includes(DestinationVersions.STANDARD)
          ? StandardDestinationsAPI.getExistingDestinations().then(res => res.data)
          : Promise.resolve([]),
        versions.includes(DestinationVersions.EDGE)
          ? DestinationAPI.getDestinations().then(res => DestinationListFactory(res.data))
          : Promise.resolve([]),
        shouldIncludeDraftStandardDestinations
          ? StandardDestinationsAPI.getDraftDestinationList().then(
            res => (res.data || [])?.map((draftDest: any) => ({ ...draftDest, isDraft: true }))
          )
          : Promise.resolve([])
      ]);

      standardDestinations = sortDestinationsBasedOnError([...standardDestinations, ...draftDestinations]);

      if (typeof filterMethod === 'function') {
        standardDestinations = filterMethod(standardDestinations);
        edgeDestinations = filterMethod(edgeDestinations);
      }

      const destinationsTilesData = [
        ...ExistingEdgeDestinationTilesFactory(edgeDestinations),
        ...DestinationTilesFactory(standardDestinations, copyModeDestinations.includes(DestinationVersions.STANDARD))
      ];

      setAllDestinations(destinationsTilesData);
      filterDestinations(destinationsTilesData, searchTerm);
      setLoading(false);

      if (!destinationsTilesData?.length) {
        skipStep();
      }
    } catch (error) {
      setLoading(false);
      setApiError(error);
    }
  };

  useEffect(() => {
    getDestinations();
  }, []);

  const tileClickHandler = destination => {
    if (destination.error) {
      analyticsTracker.eventTrack({
        action: TRACKER_CLICK_DISABLED_DESTINATION,
        properties: {
          destType: destination.dest_type
        }
      });
      return;
    }

    if (destinationMode === DestinationMode.WAREHOUSE) {
      validateWarehousePermissions(destination);
      return;
    }

    analyticsTracker.eventTrack({
      action: !destination.isDraft
        ? TRACKER_SELECT_EXISTING_DEST_TYPE
        : TRACKER_SELECT_EXISTING_DRAFT_DESTINATION_ITEM,
      properties: {
        destType: destination.dest_type,
        context: hevoEntityType.toString(),
        versions: versions.join(',')
      }
    });

    if (destination.is_copy_mode) {
      confirm({
        title: 'Create a New Edge Destination',
        body: 'You have selected a Standard Destination. Continuing will automatically create an Edge Destination using your Standard Destination’s configuration.',
        positiveButtonText: 'Continue',
        negativeButtonText: 'Cancel'
        
      }).then((confirmed) => {
        if (confirmed) {
          onCopyStandardDestinationClick(destination.id, false);
        }
      });
      
      return;
    }

    onDestinationSelect(destination);
  };

  const filterDestinations = (destinationsTilesData, searchInput) => {
    const searchedDestinations = destinationsTilesData.filter(destination =>
      matchPattern(
        [
          destination.data.name,
          destination.data.destinationType?.destinationType,
          destination.data.dest_type_display
        ],
        searchInput
      )
    );

    setFilteredDestinations(searchedDestinations);

    if (searchedDestinations.length === 0) {
      analyticsTracker.eventTrack({
        action: TRACKER_DESTINATION_LIST_EMPTY_RESULT,
        properties: {
          context: hevoEntityType.toString(),
          versions: versions.join(',')
        }
      });
    }
  };

  const onSearch = useCallback(
    (searchInput: string) => {
      saveSearch(searchInput);
      setSearchTerm(searchInput);

      if (searchInput.trim().length === 0) {
        setFilteredDestinations(allDestinations);
        return;
      }

      filterDestinations(allDestinations, searchInput);
    },
    [allDestinations, saveSearch]
  );

  const isDestinationValidatingPermission = destId =>
    permissionsObject.validatingPermissionForDestId === destId.toString();

  const validateWarehousePermissions = (dest: any) => {
    abort();
    setPermissionsObject({
      validatingPermissionForDestId: dest.id.toString(),
      validatingPermission: true,
      permissionsError: false
    });

    StandardDestinationsAPI.testActivateWarehouseConnection(dest.type, dest.id, {
      signal: abortController.signal
    })
      .then(() => {
        setPermissionsObject(prev => ({
          ...prev,
          validatingPermissionForDestId: null,
          validatingPermission: false
        }));

        analyticsTracker.eventTrack({
          action: !dest.isDraft
            ? TRACKER_SELECT_EXISTING_DEST_TYPE
            : TRACKER_SELECT_EXISTING_DRAFT_DESTINATION_ITEM,
          properties: {
            destType: dest.dest_type,
            context: hevoEntityType.toString(),
            versions: versions.join(',')
          }
        });

        onDestinationSelect(dest);
      })
      .catch(() => {
        setPermissionsObject(prev => ({
          ...prev,
          permissionsError: true,
          validatingPermission: false
        }));
      });
  };

  const onPermissionRetryBtnClick = () => {
    if (!permissionsObject.validatingPermissionForDestId) {
      return;
    }

    const selectedDest = getDestinationBeingValidatedForPermission();

    tileClickHandler(selectedDest);

    analyticsTracker.eventTrack({
      action: TRACKER_DESTINATION_PERMISSION_VALIDATION_RETRY_CLICK,
      properties: {
        destType: selectedDest.data.dest_type,
        context: hevoEntityType.toString(),
        versions: versions.join(',')
      }
    });
  };

  const getDestinationBeingValidatedForPermission = () => {
    const [selectedDestination] = filteredDestinations.filter(
      destination => destination.id === permissionsObject.validatingPermissionForDestId
    );

    return selectedDestination;
  };

  const getWarehouseRetryDocLink = () => {
    const selectedDest = getDestinationBeingValidatedForPermission();

    if (DESTINATION_TYPES[selectedDest.data.dest_type].warehousePermissionsDocLink) {
      return DESTINATION_TYPES[selectedDest.data.dest_type].warehousePermissionsDocLink;
    }

    if (DESTINATION_TYPES[selectedDest.data.dest_type].warehouseDocLink) {
      return DESTINATION_TYPES[selectedDest.data.dest_type].warehouseDocLink;
    }

    return DESTINATION_TYPES[selectedDest.data.dest_type].docLink;
  };

  const addDestination = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_ADD_NEW_DESTINATION_CLICK,
      properties: {
        context: hevoEntityType.toString(),
        versions: versions.join(',')
      }
    });

    createNewClick();
  };

  const onCopyStandardDestinationClick = (destinationId: number, isEdge: boolean) => {
    analyticsTracker.eventTrack({
      action: TRACKER_COPY_STANDARD_DESTINATION_CLICK,
      properties: {
        context: hevoEntityType.toString(),
        versions: versions.join(','),
        destinationId,
        isEdge
      }
    });

    onDestinationCopy(destinationId, isEdge);
  };

  return (
    <div
      className={styles.selectDestinationContainer}
      data-id={dataIdGenerator(hevoEntityType.name)}
    >
      <div className='scrollable-content bg-surface' id='scrollable-view' ref={setScrollElement}>
        <div className='body-container'>
          {isUpgradingPipeline ? (
            <div style={{ maxWidth: '820px', margin: 'auto' }}>
              <UpgradePipelineBanner
                standardPipelineInfo={standardPipelineInfo}
                isObjectConfigPage={false}
              />
            </div>
          ) : null}

          <div className='header-container mb-5'>
            <div className='header'>Select {destinationKeywords.singular}</div>

            <div className='subheader'>
              Select an existing {destinationKeywords.singular} or add a new one
            </div>
          </div>

          <StickySearchWrapper>
            <SearchArea
              dataId={dataIdGenerator('')}
              autofocus
              onSearch={onSearch}
              debounceInterval={DEBOUNCE_INTERVAL}
              placeholder={`Search ${destinationKeywords.plural}`}
              collapsible={false}
              defaultSearch={defaultSearch}
            />
          </StickySearchWrapper>

          <div className='node-container'>
            {filteredDestinations.length ? (
              <TileGrid
                tileHeight={DEFAULT_TILE_HEIGHT}
                tiles={filteredDestinations}
                onTileClick={tileClickHandler}
                TileComponent={DestinationTile}
                scrollElement={scrollElement}
                tileComponentProps={tile => ({
                  canUseEnterpriseNodeLogo: tile.metaData.enterpriseNodeLogo,
                  isValidating:
                    isDestinationValidatingPermission(tile.id) &&
                    permissionsObject.validatingPermission,
                  validationError:
                    isDestinationValidatingPermission(tile.id) &&
                    permissionsObject.permissionsError,
                  selected:
                    isDestinationValidatingPermission(tile.id) &&
                    permissionsObject.validatingPermission,
                  faded:
                    !isDestinationValidatingPermission(tile.id) &&
                    permissionsObject.validatingPermission,
                  selectedError:
                    isDestinationValidatingPermission(tile.id) && permissionsObject.permissionsError
                })}
              />
            ) : null}

            {showNoMatchAlert ? (
              <NoEntityTypeAlert suggestionContext={suggestionContext} isEntityType={false} />
            ) : null}

            {loading ? <TileSkeletonGrid tileCount={4} /> : null}

            {showPageError ? (
              <RetryApiAlert
                actionHandler={() => {
                  getDestinations();
                }}
                error={apiError}
                dataId={dataIdGenerator('')}
              />
            ) : null}
          </div>
        </div>
      </div>

      {permissionsObject.permissionsError ? (
        <div className={styles.errorMessageContainer}>
          <HdPane
            dataId={dataIdGenerator('permissions-error')}
            variant='error-faded'
            iconClasses='error-filled'
            className={`${styles.errorMessagePane}`}
          >
            <span className='text-medium'>
              You must provide additional permissions to the configured database user to use the
              Destination as a Warehouse.{' '}
              <HdDocLink
                docLink={getWarehouseRetryDocLink()}
                section='activate_select_warehouse'
                size='md'
                label='Read More'
                dataId={dataIdGenerator('warehouse')}
              />
            </span>

            <HdButton
              type='button'
              size='sm'
              variation='outline'
              palette='error'
              icon='refresh'
              className='ml-4'
              onClick={() => onPermissionRetryBtnClick()}
              dataId={dataIdGenerator('warehouse-permissions-retry')}
            >
              Retry
            </HdButton>
          </HdPane>
        </div>
      ) : null}

      <div className='fixed-footer flex-shrink-0' data-id={dataIdGenerator('footer')}>
        <div className='center-flex-row text-secondary'>
          Click here to add a new {destinationKeywords.singular}
        </div>

        <HdRbacButton
          rbacPermission={RbacPermissions.DESTINATION_CREATE}
          icon='plus'
          onClick={addDestination}
          className='ml-5'
          dataId={dataIdGenerator('add-new')}
        >
          Add {destinationKeywords.singular}
        </HdRbacButton>
      </div>
    </div>
  );
}
